const COLOR_SCALES = {
    "green-yellow-red": {
        colors: ["#00ff00", "#ffff00", "#ff0000"],
        name: "Yeşil-Sarı-Kırmızı",
    },
    "red-yellow-green": {
        colors: ["#ff0000", "#ffff00", "#00ff00"],
        name: "Kırmızı-Sarı-Yeşil",
    },
    "blue-white-red": {
        colors: ["#0000ff", "#ffffff", "#ff0000"],
        name: "Mavi-Beyaz-Kırmızı",
    },
    "white-blue": {
        colors: ["#ffffff", "#0000ff"],
        name: "Beyaz-Mavi",
    },
    "white-red": {
        colors: ["#ffffff", "#ff0000"],
        name: "Beyaz-Kırmızı",
    },
    "green-blue": {
        colors: ["#00ff00", "#0000ff"],
        name: "Yeşil-Mavi",
    },
};
/**
 * Pivot Color Engine
 * Pivot tablo için renklendirme hesaplamaları yapar
 */

export class PivotColorEngine {
    /**
     * RGB rengini hex formatına çevirir
     */
    static rgbToHex(r, g, b) {
        const toHex = (c) => {
            const hex = Math.round(c).toString(16);
            return hex.length === 1 ? "0" + hex : hex;
        };
        return "#" + toHex(r) + toHex(g) + toHex(b);
    }

    /**
     * Hex rengi RGB'ye çevirir
     */
    static hexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
            ? {
                  r: parseInt(result[1], 16),
                  g: parseInt(result[2], 16),
                  b: parseInt(result[3], 16),
              }
            : null;
    }

    /**
     * İki renk arasında interpolasyon yapar
     */
    static interpolateColor(color1, color2, factor) {
        const c1 = this.hexToRgb(color1);
        const c2 = this.hexToRgb(color2);

        if (!c1 || !c2) return color1;

        const r = c1.r + factor * (c2.r - c1.r);
        const g = c1.g + factor * (c2.g - c1.g);
        const b = c1.b + factor * (c2.b - c1.b);

        return this.rgbToHex(r, g, b);
    }

    /**
     * Predefined color scales
     */

    /**
     * Heat map için renk hesaplar
     * @param {number} value - Değer
     * @param {number} min - Minimum değer
     * @param {number} max - Maximum değer
     * @param {string} scale - Renk skalası
     */
    static getHeatMapColor(value, min, max, scale = "green-yellow-red") {
        if (value === null || value === undefined || isNaN(value)) {
            return "#ffffff";
        }

        if (min === max) {
            return COLOR_SCALES[scale].colors[0];
        }

        // Normalize value to 0-1
        const normalizedValue = (value - min) / (max - min);

        const scaleColors = COLOR_SCALES[scale].colors;

        if (scaleColors.length === 2) {
            // İki renkli scale
            return this.interpolateColor(scaleColors[0], scaleColors[1], normalizedValue);
        } else if (scaleColors.length === 3) {
            // Üç renkli scale
            if (normalizedValue < 0.5) {
                return this.interpolateColor(scaleColors[0], scaleColors[1], normalizedValue * 2);
            } else {
                return this.interpolateColor(scaleColors[1], scaleColors[2], (normalizedValue - 0.5) * 2);
            }
        }

        return scaleColors[0];
    }

    /**
     * Manuel renk kurallarını değerlendirir
     * @param {number} value - Değer
     * @param {Array} rules - Renk kuralları
     */
    static getManualRuleColor(value, rules) {
        if (!rules || rules.length === 0) return null;

        // Kuralları öncelik sırasına göre kontrol et
        for (const rule of rules) {
            if (this.evaluateCondition(value, rule.condition)) {
                return rule.color;
            }
        }

        return null;
    }

    /**
     * Koşulu değerlendirir
     */
    static evaluateCondition(value, condition) {
        if (value === null || value === undefined || isNaN(value)) {
            return false;
        }

        const numValue = parseFloat(value);
        const conditionValue = parseFloat(condition.value);

        switch (condition.operator) {
            case ">":
                return numValue > conditionValue;
            case ">=":
                return numValue >= conditionValue;
            case "<":
                return numValue < conditionValue;
            case "<=":
                return numValue <= conditionValue;
            case "=":
            case "==":
                return numValue === conditionValue;
            case "!=":
                return numValue !== conditionValue;
            case "between":
                return numValue >= condition.minValue && numValue <= condition.maxValue;
            default:
                return false;
        }
    }

    /**
     * Hücre için renk hesaplar (manuel kurallar öncelikli, sonra heat map)
     */
    static getCellColor(value, colorSettings, min, max) {
        if (!colorSettings) return null;

        // Önce manuel kuralları kontrol et
        if (colorSettings.manualRules && colorSettings.manualRules.length > 0) {
            const manualColor = this.getManualRuleColor(value, colorSettings.manualRules);
            if (manualColor) return manualColor;
        }

        // Manuel kural yoksa ve heat map aktifse, heat map rengi döndür
        if (colorSettings.heatMap && colorSettings.heatMap.enabled) {
            return this.getHeatMapColor(value, min, max, colorSettings.heatMap.scale);
        }

        return null;
    }

    /**
     * Rengin açık mı koyu mu olduğunu belirler (metin rengi için)
     */
    static isColorLight(hexColor) {
        const rgb = this.hexToRgb(hexColor);
        if (!rgb) return true;

        // Relative luminance hesapla
        const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
        return luminance > 0.5;
    }

    /**
     * Arka plan rengine göre uygun metin rengi döndürür
     */
    static getTextColor(backgroundColor) {
        if (!backgroundColor) return "#000000";
        return this.isColorLight(backgroundColor) ? "#000000" : "#ffffff";
    }
}
PivotColorEngine.COLOR_SCALES = COLOR_SCALES;

export default PivotColorEngine;
